import React, { useState, useEffect } from 'react';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { schemaExtensionRegistry } from '../../DocsApp/plugins/schemaExtensionRegistry';
import { hotkeyRegistry } from '../../DocsApp/plugins/hotkeyRegistry';
import { citationMarkSpec } from './citationMark';
import { bibliographyNodeSpec } from './bibliographyNode';
import { citationTooltipPlugin } from './citationTooltipPlugin';
import { citationCleanupPlugin } from './citationCleanupPlugin';
import { createFileLogger } from '../../../utils/logger';
import { LibraryPDF, ReferenceEntity, getDisplayTitle, getAuthorStrings, extractYear } from '../types/reference';
import { formatCitation } from '../utils/citationFormatter';
import { getCitationConfig } from '../config/citationConfig';
import useEntityStore from '../../../stores/entityStore';
import { useCommandRegistryStore } from '../../../stores/commandRegistryStore';
import { getWindowSizeAndPosition } from '../../../components/Space/utils/windowUtils';
import type { EditorView } from 'prosemirror-view';
import fetchService from '../../../services/fetchService';
import { API_BASE_URL } from '../../../api/config';
import './LibraryDocsPlugin.scss';

const log = createFileLogger('LIBRARY_DOCS_PLUGIN');

/**
 * Helper: Get citation data from Y.js Map
 * Citations store their data in Y.js Map (like comments) to avoid mark attribute issues
 */
export function getCitationData(ydoc: any, citationId: string): {
  referenceIds: string[];
  formattedText: string;
  references: Array<{
    id: string;
    title: string;
    authors: string[];
    year: string | number;
    journal?: string;
    volume?: string;
    issue?: string;
    pages?: string;
    doi?: string;
    url?: string;
    primaryPdfId?: string;
  }>;
  createdAt: number;
} | null {
  try {
    if (!ydoc) return null;
    const citationsMap = ydoc.getMap('citations');
    const citationData = citationsMap.get(citationId);
    return citationData || null;
  } catch {
    return null;
  }
}

/**
 * Insert citation at current cursor position
 * This is the plugin's internal command for inserting citations
 */
export function insertCitation(view: EditorView, references: ReferenceEntity[], ydoc: any, docId?: string): boolean {
  if (!view || !view.state.schema.marks.citation) {
    console.error('[LibraryDocsPlugin] Citation mark not available in schema');
    return false;
  }
  
  // Format citation text directly from passed references (no re-fetch needed)
  (async () => {
    try {
      // Check if view is still valid
      if (!view || !view.state) {
        console.error('[LibraryDocsPlugin] Editor view no longer available');
        return;
      }
      
      // Get citation configuration for this document
      const config = getCitationConfig();
      
      // Format citation using configured style
      const citationText = formatCitation(references, config.style);
      
      console.log('[LibraryDocsPlugin] Formatted citation:', {
        citationText,
        style: config.style,
        referenceCount: references.length,
        references: references.map(r => ({
          id: r._id,
          authors: r.skeleton?.authors,
          year: r.skeleton?.year
        }))
      });
      
      const citationId = `cite-${Date.now()}`;
      const { state, dispatch } = view;
      const { schema } = state;
      const { from, to } = state.selection;
      
      // Use proper citation mark with citationId attribute (avoiding naming conflict with node.attrs.id)
      const citationMark = schema.marks.citation;
      if (!citationMark) {
        console.error('[LibraryDocsPlugin] Citation mark not found in schema');
        return;
      }
      
      // Extract reference IDs
      const referenceIds = references.map(r => r._id);
      
      // Store citation metadata in Y.js Map for hover/click functionality
      if (ydoc) {
        try {
          const citationsMap = ydoc.getMap('citations');
          
          const citationData = {
            referenceIds,
            formattedText: citationText,
            references: references.map(r => {
              console.log('[LibraryDocsPlugin] Storing reference in citations Map:', {
                id: r._id,
                name: r.name,
                skeleton: r.skeleton
              });
              
              return {
                id: r._id,
                title: r.skeleton?.title || r.name,
                authors: r.skeleton?.authors || [],
                year: r.skeleton?.year,
                journal: r.skeleton?.journal,
                volume: r.skeleton?.volume,
                issue: r.skeleton?.issue,
                pages: r.skeleton?.pages,
                doi: r.skeleton?.doi,
                url: r.skeleton?.url,
                primaryPdfId: r.skeleton?.primaryPdfId
              };
            }),
            createdAt: Date.now()
          };
          
          console.log('[LibraryDocsPlugin] Full citation data to store:', citationData);
          
          citationsMap.set(citationId, citationData);
          log.info('[LibraryDocsPlugin] Citation metadata stored');
        } catch (error) {
          log.error('[LibraryDocsPlugin] Failed to store citation metadata:', error);
        }
      }
      
      // Apply citation mark
      const mark = citationMark.create({
        citationId: citationId,
        resolved: false
      });
      
      // Insert text with mark
      const tr = state.tr
        .insertText(citationText, from, to)
        .addMark(from, from + citationText.length, mark);
      
      dispatch(tr);
      
      log.info('[LibraryDocsPlugin] Citation inserted:', { 
        citationId, 
        referenceIds: references.map(r => r._id) 
      });
      
      // Track citation relationship (if docId provided)
      if (docId && referenceIds.length > 0) {
        try {
          await fetchService(`${API_BASE_URL}/citations/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              doc_id: docId,
              pdf_ids: referenceIds,
              citation_context: citationText
            })
          });
          log.info('[LibraryDocsPlugin] Citation tracking updated for', referenceIds.length, 'PDFs');
        } catch (trackingError) {
          // Non-critical - citation was inserted successfully
          log.error('[LibraryDocsPlugin] Failed to track citation (non-critical):', trackingError);
        }
      } else if (!docId) {
        log.warn('[LibraryDocsPlugin] No docId provided, skipping citation tracking');
      }
    } catch (error) {
      console.error('[LibraryDocsPlugin] Failed to insert citation:', error);
    }
  })();
  
  return true;
}

/**
 * Library plugin for DocsApp - enables citation insertion
 * 
 * Features:
 * - Adds 'citation' mark to ProseMirror schema
 * - Registers Mod-Shift-C hotkey for quick citation insertion
 * - Provides citation search UI
 */

/**
 * Get ProseMirror plugins to inject into editor
 * Call this when building editor plugins
 */
export function getLibraryDocsProseMirrorPlugins(ydoc: any) {
  return [
    citationTooltipPlugin(ydoc),
    citationCleanupPlugin(ydoc)
  ];
}

/**
 * Initialize the Library Docs plugin
 * Call this before DocsApp creates the ProseMirror editor
 */
export function initializeLibraryDocsPlugin() {
  console.log('[LibraryDocsPlugin] Initializing plugin...');
  
  // Register citation mark with schema
  schemaExtensionRegistry.registerMark('citation', citationMarkSpec);
  
  // Register bibliography node with schema
  schemaExtensionRegistry.registerNode('bibliography', bibliographyNodeSpec);
  
  console.log('[LibraryDocsPlugin] Schema extensions registered:', {
    registeredMarks: schemaExtensionRegistry.getRegisteredMarks(),
    registeredNodes: schemaExtensionRegistry.getRegisteredNodes(),
    hasCitation: schemaExtensionRegistry.getRegisteredMarks().includes('citation'),
    hasBibliography: schemaExtensionRegistry.getRegisteredNodes().includes('bibliography')
  });
  
  log.info('[LibraryDocsPlugin] Citation mark and bibliography node registered');
  
  // Register hotkey for citation insertion
  hotkeyRegistry.register({
    id: 'library-insert-citation',
    keys: 'Mod-Shift-c',
    description: 'Insert citation from Library',
    handler: (view) => {
      // Verify mark is in schema at runtime
      if (!view.state.schema.marks.citation) {
        console.error('[LibraryDocsPlugin] CRITICAL: Citation mark NOT in editor schema!', {
          availableMarks: Object.keys(view.state.schema.marks),
          schemaMarkCount: Object.keys(view.state.schema.marks).length
        });
        return false;
      }
      
      console.log('[LibraryDocsPlugin] Citation mark verified in schema');
      
      // Get cursor position from editor view
      const { from } = view.state.selection;
      const coords = view.coordsAtPos(from);
      
      // Open citation search modal at cursor position
      const event = new CustomEvent('library:open-citation-search', {
        detail: { 
          editorView: view,
          position: {
            x: coords.left,
            y: coords.bottom + 4 // Add small offset below cursor
          }
        }
      });
      window.dispatchEvent(event);
      return true;
    }
  });
  
  log.info('[LibraryDocsPlugin] Hotkey registered: Mod-Shift-C');
}

/**
 * Citation search popover component
 * Opens when user presses Mod-Shift-C or clicks citation button
 */
interface CitationSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (references: ReferenceEntity[]) => void;
  spaceId: string;
  position?: { x: number; y: number };
}

export const CitationSearchModal: React.FC<CitationSearchModalProps> = ({
  isOpen,
  onClose,
  onInsert,
  spaceId,
  position
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [pdfs, setPdfs] = useState<LibraryPDF[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const { entities, actions: entityActions } = useEntityStore();
  const { executeCommand } = useCommandRegistryStore();
  
  // Load PDFs with referenceMetadata
  useEffect(() => {
    if (!isOpen) return;
    
    const libraryPdfs = entities.filter(e => 
      e.entityType === 'File.Document.PDF' && 
      e.spaceId === spaceId &&
      (e as any).skeleton?.referenceMetadata
    ) as LibraryPDF[];
    
    setPdfs(libraryPdfs);
  }, [isOpen, entities, spaceId]);
  
  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedIds(new Set());
      setHighlightedIndex(0);
    }
  }, [isOpen]);
  
  // Filter PDFs by search
  const filteredPdfs = pdfs.filter(pdf => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const metadata = pdf.skeleton.referenceMetadata;
    const authors = getAuthorStrings(pdf);
    const searchableText = [
      pdf.name || '',
      metadata?.title || '',
      ...authors
    ].join(' ').toLowerCase();
    
    return searchableText.includes(query);
  }).slice(0, 8); // Limit to 8 results for compact view
  
  const handleToggleSelection = (pdfId: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(pdfId)) {
      newSelection.delete(pdfId);
    } else {
      newSelection.add(pdfId);
    }
    setSelectedIds(newSelection);
  };
  
  const handleInsert = async () => {
    if (selectedIds.size === 0) return;
    
    log.info('[CitationSearchModal] Inserting citations for', selectedIds.size, 'PDFs');
    
      // NEW SIMPLIFIED APPROACH: PDFs ARE references, no separate entity needed
      // Just use the PDFs directly with their referenceMetadata
      const references: any[] = [];  // Use 'any' type since we're adapting PDFs for citation
      
      for (const pdfId of Array.from(selectedIds)) {
        const pdf = pdfs.find(p => p._id === pdfId);
        if (!pdf || !pdf.skeleton.referenceMetadata) continue;
        
        const metadata = pdf.skeleton.referenceMetadata;
        
        console.log('[CitationSearchModal] Using PDF directly as reference:', {
          pdfId,
          pdfName: pdf.name,
          title: metadata.title,
          authors: metadata.authors
        });
        
        // Convert PDF to citation-compatible format
        // The PDF itself IS the reference - no duplication needed
        const referenceFromPdf = {
          ...pdf,
          skeleton: {
            ...pdf.skeleton,
            // Map referenceMetadata to skeleton fields for citation formatter
            title: metadata.title,
            authors: metadata.authors?.map((a: any) => a.fullName || `${a.lastName}, ${a.firstName}`) || [],
            year: extractYear(pdf),  // Extract year from date string
            journal: metadata.publication,
            volume: metadata.volume,
            issue: metadata.issue,
            pages: metadata.pages,
            doi: metadata.DOI,
            url: metadata.url,
            primaryPdfId: pdf._id  // Reference back to self
          }
        };
        
        references.push(referenceFromPdf);
        log.info('[CitationSearchModal] Using PDF as reference:', pdf._id);
      }
    
    if (references.length > 0) {
      onInsert(references);
      setSelectedIds(new Set());
      setSearchQuery('');
      onClose();
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIds.size > 0) {
        handleInsert();
      } else if (filteredPdfs.length > 0) {
        // Quick insert: if nothing selected, insert the highlighted one
        const pdf = filteredPdfs[highlightedIndex];
        setSelectedIds(new Set([pdf._id]));
        setTimeout(() => handleInsert(), 0);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, filteredPdfs.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === ' ' && filteredPdfs.length > 0) {
      e.preventDefault();
      handleToggleSelection(filteredPdfs[highlightedIndex]._id);
    }
  };
  
  if (!isOpen) return null;
  
  // Calculate popover position with viewport boundary checks
  let popoverStyle: React.CSSProperties = {};
  if (position) {
    const popoverWidth = 420;
    const popoverHeight = 320;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let x = position.x;
    let y = position.y;
    
    // Adjust if popover goes off right edge
    if (x + popoverWidth > viewportWidth) {
      x = viewportWidth - popoverWidth - 16;
    }
    
    // Adjust if popover goes off bottom edge
    if (y + popoverHeight > viewportHeight) {
      y = position.y - popoverHeight - 8; // Show above cursor instead
    }
    
    // Ensure minimum margins
    x = Math.max(16, x);
    y = Math.max(16, y);
    
    popoverStyle = {
      position: 'fixed',
      left: `${x}px`,
      top: `${y}px`,
    };
  }
  
  return (
    <>
      <div className="library-citation-popover-backdrop" onClick={onClose} />
      <div 
        ref={popoverRef}
        className="library-citation-popover" 
        style={popoverStyle}
        onKeyDown={handleKeyDown}
      >
        <div className="library-citation-popover__search-container">
          <input
            type="text"
            placeholder="Search Library..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="library-citation-popover__search"
            autoFocus
          />
          <button
            className="library-citation-popover__open-library-btn"
            onClick={(e) => {
              e.stopPropagation();
              const { size, position: windowPosition } = getWindowSizeAndPosition();
              executeCommand('space_createWindow', {
                spaceId,
                appType: 'library',
                title: 'Library',
                position: windowPosition,
                size: { width: 900, height: 700 },
                callback: (result: string) => {
                  log.info('[CitationSearchModal] Library window created');
                }
              });
            }}
            title="Open Library"
          >
            <ArrowSquareOut size={14} weight="regular" />
          </button>
        </div>
        
        <div className="library-citation-popover__list">
          {filteredPdfs.length === 0 ? (
            <div className="library-citation-popover__empty">
              {pdfs.length === 0 ? 'No papers' : 'No matches'}
            </div>
          ) : (
            filteredPdfs.map((pdf, index) => {
              const title = getDisplayTitle(pdf);
              const authors = getAuthorStrings(pdf);
              const year = extractYear(pdf);
              const journal = pdf.skeleton.referenceMetadata?.publication;
              
              // Format author citation: "FirstAuthor et al., Year" or just "FirstAuthor, Year"
              let authorCitation = '';
              if (authors.length > 0) {
                const firstAuthor = authors[0].split(',')[0]; // Get last name only
                if (authors.length > 1) {
                  authorCitation = `${firstAuthor} et al.`;
                } else {
                  authorCitation = firstAuthor;
                }
              } else {
                authorCitation = 'Unknown';
              }
              
              return (
                <div
                  key={pdf._id}
                  className={`library-citation-popover__item ${
                    selectedIds.has(pdf._id) ? 'library-citation-popover__item--selected' : ''
                  } ${
                    index === highlightedIndex ? 'library-citation-popover__item--highlighted' : ''
                  }`}
                  onClick={() => handleToggleSelection(pdf._id)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="library-citation-popover__item-check">
                    {selectedIds.has(pdf._id) && '✓'}
                  </div>
                  <div className="library-citation-popover__item-content">
                    <div className="library-citation-popover__item-title">
                      {title}
                    </div>
                    <div className="library-citation-popover__item-meta">
                      {authorCitation}
                      {year && `, ${year}`}
                      {journal && (
                        <>
                          {' • '}
                          <span style={{ fontStyle: 'italic' }}>{journal}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        
        {selectedIds.size > 0 && (
          <div className="library-citation-popover__footer">
            <span>{selectedIds.size} selected</span>
            <button onClick={handleInsert}>Insert</button>
          </div>
        )}
      </div>
    </>
  );
};

